import React, {Suspense, memo} from "react"
import {breakpoints} from "@next/ux-fabric"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest*"
import {useMediaQuery} from "../../hooks/useMediaQuery"
import connect from "./connect"
import {type DrawerProps} from "../SecondaryNavDrawer"

const SecondaryNavDrawer = React.lazy(() => import("../SecondaryNavDrawer"))
const SecondaryNavOverlay = React.lazy(() => import("../SecondaryNavOverlay"))
const SecondaryNavContent = React.lazy(() => import("../SecondaryNavContent"))

export type SecondaryNavProps = Pick<DrawerProps, "anchor"> & {
    open: boolean
    deactivateIndex: () => void
    isInSecondaryNav: boolean
    isInPrimaryNav: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    isPrimaryNavbarLinkActive: boolean
}

export const SecondaryNav = memo(
    ({
        anchor,
        open: isDrawerOpen,
        deactivateIndex,
        isInSecondaryNav,
        isInPrimaryNav,
        templating,
        isPrimaryNavbarLinkActive,
    }: SecondaryNavProps) => {
        const isOpen = isInSecondaryNav || isInPrimaryNav
        const isLargeUp = useMediaQuery(`(min-width: ${breakpoints.values.lg}px)`)
        const {mainTemplate, fallbackTemplate} = templating
        const TemplatedContainer = useTemplatedComponent({mainTemplate, fallbackTemplate}, MANIFEST)
        return (
            <>
                {isDrawerOpen && !isLargeUp ? (
                    <Suspense>
                        <SecondaryNavDrawer
                            showCloseIcon={false}
                            anchor={anchor}
                            open={isDrawerOpen && !isPrimaryNavbarLinkActive}
                            onClose={deactivateIndex}
                        >
                            <Suspense>
                                <TemplatedContainer data-testid="secondary-meganav-narrow">
                                    <SecondaryNavContent showAsWideView={false} />
                                </TemplatedContainer>
                            </Suspense>
                        </SecondaryNavDrawer>
                    </Suspense>
                ) : null}
                {isOpen && isLargeUp ? (
                    <Suspense>
                        <SecondaryNavOverlay
                            open={isOpen}
                            handleClose={deactivateIndex}
                            hideOverlay={isPrimaryNavbarLinkActive}
                        >
                            <SecondaryNavContent showAsWideView />
                        </SecondaryNavOverlay>
                    </Suspense>
                ) : null}
            </>
        )
    },
)

SecondaryNav.displayName = "SecondaryNav"

export default connect(SecondaryNav)
